import { useQuery, useMutation } from '@tanstack/react-query';
import { Shield, CheckCircle, XCircle, Eye, Trash2 } from 'lucide-react';
import { useAuth } from '@/components/AuthProvider';
import { apiRequest, queryClient } from '@/lib/queryClient';
import type { Business } from '@shared/schema';

export default function AdminPage() {
  const { user, isAdmin } = useAuth();

  const { data: pendingBusinesses = [], isLoading } = useQuery({
    queryKey: ['/api/businesses', { approved: false }],
    queryFn: () => fetch('/api/businesses?approved=false').then(res => res.json()) as Promise<Business[]>,
    enabled: isAdmin
  });

  const { data: approvedBusinesses = [] } = useQuery({
    queryKey: ['/api/businesses', { approved: true }],
    queryFn: () => fetch('/api/businesses?approved=true').then(res => res.json()) as Promise<Business[]>,
    enabled: isAdmin
  });

  const approveMutation = useMutation({
    mutationFn: (businessId: string) => 
      apiRequest(`/api/businesses/${businessId}/approve`, { method: 'PATCH' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/businesses'] });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (businessId: string) => 
      apiRequest(`/api/businesses/${businessId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/businesses'] });
    }
  });

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield size={48} className="mx-auto text-gray-400 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">Please sign in to access the admin panel.</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield size={48} className="mx-auto text-red-400 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Admin Access Required</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  const handleApprove = (businessId: string) => {
    approveMutation.mutate(businessId);
  };

  const handleDelete = (businessId: string) => {
    if (confirm('Are you sure you want to delete this business? This action cannot be undone.')) {
      deleteMutation.mutate(businessId);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center space-x-3">
            <Shield size={32} className="text-red-700" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600">Manage business listings and moderation</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-full">
                <Shield size={24} className="text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending Review</p>
                <p className="text-2xl font-bold text-gray-900">{pendingBusinesses.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 bg-red-100 rounded-full">
                <CheckCircle size={24} className="text-red-700" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Approved</p>
                <p className="text-2xl font-bold text-gray-900">{approvedBusinesses.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-full">
                <Eye size={24} className="text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Businesses</p>
                <p className="text-2xl font-bold text-gray-900">
                  {pendingBusinesses.length + approvedBusinesses.length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Pending Businesses */}
        <div className="bg-white rounded-lg shadow-md mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <Shield size={20} className="mr-2" />
              Pending Approval ({pendingBusinesses.length})
            </h2>
          </div>

          {isLoading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading businesses...</p>
            </div>
          ) : pendingBusinesses.length === 0 ? (
            <div className="p-6 text-center">
              <CheckCircle size={48} className="mx-auto text-red-500 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">All Caught Up!</h3>
              <p className="text-gray-600">No businesses pending approval.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {pendingBusinesses.map((business) => (
                <div key={business.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {business.name}
                      </h3>
                      <p className="text-gray-600 mb-2">{business.description}</p>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                          {business.category}
                        </span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">
                          {business.city}, {business.state}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500">
                        <p>{business.address}</p>
                        {business.phone && <p>Phone: {business.phone}</p>}
                        {business.email && <p>Email: {business.email}</p>}
                        {business.website && (
                          <p>
                            Website: 
                            <a 
                              href={business.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline ml-1"
                            >
                              {business.website}
                            </a>
                          </p>
                        )}
                        <p className="mt-2">
                          Submitted: {new Date(business.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col space-y-2 ml-4">
                      <button
                        onClick={() => handleApprove(business.id)}
                        disabled={approveMutation.isPending}
                        className="flex items-center space-x-2 px-4 py-2 bg-red-700 text-white rounded-md hover:bg-red-800 disabled:bg-gray-400 transition-colors"
                      >
                        <CheckCircle size={16} />
                        <span>Approve</span>
                      </button>
                      
                      <button
                        onClick={() => handleDelete(business.id)}
                        disabled={deleteMutation.isPending}
                        className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 transition-colors"
                      >
                        <XCircle size={16} />
                        <span>Reject</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Approved Businesses */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <CheckCircle size={20} className="mr-2 text-red-700" />
              Approved Businesses ({approvedBusinesses.length})
            </h2>
          </div>

          {approvedBusinesses.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-gray-600">No approved businesses yet.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
              {approvedBusinesses.map((business) => (
                <div key={business.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900">{business.name}</h4>
                      <p className="text-sm text-gray-600">
                        {business.category} • {business.city}, {business.state}
                      </p>
                      {business.rating > 0 && (
                        <p className="text-sm text-yellow-600">
                          ★ {business.rating.toFixed(1)} ({business.reviewCount} reviews)
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDelete(business.id)}
                      disabled={deleteMutation.isPending}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      title="Delete business"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}